// Dynamic long-tail title-tag map.
//
// The owner maintains a list of priority long-tail search phrases. Each phrase
// must be rendered as the exact <title> of exactly one automated page (guide or
// guide article) so Google sees a unique, intent-matched title tag per query
// instead of the same generic guide title repeated across pages.
//
// `scripts/title-unique-check.ts` enforces both halves of the rule: every
// phrase below resolves to a real guide/article, and no two pages on the site
// end up with the same rendered title.

import type { Guide } from "./guides-data";

export interface LongTailTitle {
  /** Guide slug. */
  guide: string;
  /** Article slug inside that guide. */
  article: string;
  /** Rendered title tag — contains the target long-tail phrase verbatim. */
  title: string;
}

export const LONGTAIL_TITLES: LongTailTitle[] = [
  { guide: "palworld-huge-scorching-egg-guide", article: "farming", title: "How to Get Massive Huge Scorching Egg Palworld" },
  { guide: "palworld-frozen-egg-guide", article: "locations", title: "Palworld Frozen Egg Locations and Hatch Times" },
  { guide: "palworld-sakurajima-map-guide", article: "farming-routes", title: "Best Farming Routes for Pure Quartz Palworld Sakurajima" },
  { guide: "katress", article: "katress-breeding-chains", title: "Katress Palworld Breeding Combinations and Work Suitability" },
  { guide: "passive-skills", article: "work-passives", title: "Best Passive Traits for Base Utility Pals Palworld" },
  { guide: "gear-partner-skill-necklace", article: "gear-partner-skill-necklace-advanced-tips", title: "How to Level Up Partner Skills Fast Palworld" },
  { guide: "palworld-card-pack-opening", article: "pull-rates", title: "Palworld Official Card Game Dawn of Palpagos Pull Rates" },
  { guide: "pal-stars-booster-pack-game", article: "complete-walkthrough", title: "How to Open Pal Stars Booster Packs Online" },
  { guide: "pal-stars-booster-pack-game", article: "palstars-grade", title: "Palworld Booster Pack Card Grading Gem Mint 10 Defects" },
  { guide: "pal-stars-palsino", article: "how-palsino-works", title: "Pal Stars Palsino Free Slot Machine Guide and FAQ" },
  { guide: "palworld-breeding-guide", article: "breed-power-and-combos", title: "How to Breed Anubis Palworld Step by Step" },
  { guide: "breeding", article: "breeding-math", title: "Palworld Shortest Breeding Path Calculator" },
  { guide: "breeding", article: "endgame-combos", title: "What Do You Get When You Breed Relaxaurus and Sparkit" },
  { guide: "palworld-breeding-guide", article: "how-breeding-works", title: "Palworld Reverse Breeding Calculator Tool" },
  { guide: "breeding-mechanics", article: "breed-power-explained", title: "Shortest Breeding Route Calculator Palworld" },
  { guide: "breeding-mechanics", article: "breeding-faq", title: "Palworld Reverse Breeding Lookup Parents Finder" },
  { guide: "palworld-fusion-breeding-combos", article: "what-is-fusion-breeding", title: "How to Breed Specific Sub Type Combinations Palworld" },
  { guide: "passive-skill-inheritance", article: "how-passive-inheritance-works", title: "Passive Trait Inheritance Chain Breeding Calculator" },
  { guide: "passive-skill-inheritance", article: "best-passives-to-breed", title: "Palworld Passive Skill Chain Planner Tools" },
  { guide: "tier-list", article: "element-matrix", title: "Palworld Elemental Type Weakness Damage Chart" },
  { guide: "palworld-team-calculator-guide", article: "team-element-coverage", title: "How Much Damage Does Fire Do to Grass Pals Palworld" },
  { guide: "palworld-team-calculator-guide", article: "team-calculator-method", title: "Palworld IV Calculator Talent Values 100" },
  { guide: "palworld-team-calculator-guide", article: "team-and-base-balance", title: "Palworld Base Stats Calculator by Level" },
  { guide: "palstars-palworld-card-game", article: "daily-free-packs", title: "Palworld Booster Pack Simulator Pull Rates" },
  { guide: "palworld-trial-deck", article: "palworld-trial-deck-card-list", title: "Palworld TCG Card Database Checklist" },
  { guide: "palworld-trial-deck", article: "palworld-trial-deck-quick-reference", title: "Official Palworld Trading Card List by Element" },
  { guide: "palworld-tcg-chase-cards", article: "palworld-tcg-chase-cards-quick-reference", title: "How to Check Palworld Card Rarity Codes" },
  { guide: "palworld-tcg-gameplay-rules", article: "rules-overview", title: "Palworld Card Game Rules and Deck Building" },
  { guide: "best-mounts", article: "fastest-fliers", title: "Fastest Flying Mount Speeds Comparison Chart Palworld" },
  { guide: "best-mounts", article: "mount-tier", title: "Palworld Mount Speed Tier List Patch Updates" },
  // Re-homed from route titles: /damage now owns "palworld damage calculator"
  // and /elements owns the type-chart term, so these two phrases moved onto
  // dedicated guide articles instead of competing on the same tool pages.
  { guide: "weapons", article: "weapons-tier", title: "Palworld Attack Stats Calculator by Level" },
  { guide: "tier-list", article: "pve-tier", title: "Palworld Elemental Type Weakness Damage Calculator" },
];

/**
 * Route titles that carry a long-tail phrase directly (checked, not applied).
 *
 * De-duplication rule: only /damage may use the "damage calculator" head term.
 * /elements previously used it too, which split the same query across two URLs
 * in Search Console, so it now owns the type-chart intent instead.
 */
export const LONGTAIL_ROUTE_TITLES: Record<string, string> = {
  "/elements": "Palworld Type Chart: Every Element Weakness",
  "/damage": "Palworld Damage Calculator: DPS & Boss Multipliers",
};

/** Applies the long-tail titles to the merged guide list. */
export function applyLongTailTitles(guides: Guide[]) {
  const bySlug = new Map(guides.map((g) => [g.slug, g]));
  for (const entry of LONGTAIL_TITLES) {
    const article = bySlug.get(entry.guide)?.articles.find((a) => a.slug === entry.article);
    if (article) article.title = entry.title;
  }
}
